/**
 * Navigation glyphs.
 *
 * Inline SVG, one stroke weight, drawn in `currentColor` so a rail item, a
 * sidebar row and a tooltip trigger all take their colour from the state they
 * are in rather than from a variant per place. No icon font, no remote asset,
 * no CSP surface — the same reasoning as `PlatformIcons`.
 *
 * These are ours. The platform marks (Facebook blue, YouTube red) live in
 * `PlatformIcons` and are deliberately not drawn in `currentColor`: they
 * identify someone else's product.
 */

type IconProps = { className?: string };

const BASE = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: "false",
} as const;

/** Every nav icon has this shape, so `nav.ts` can hold them as plain values. */
export type NavIcon = (props: IconProps) => JSX.Element;

const icon = (paths: JSX.Element): NavIcon =>
  function Icon({ className = "nav-icon" }: IconProps) {
    return <svg {...BASE} className={className}>{paths}</svg>;
  };

export const DashboardIcon = icon(
  <>
    <rect x="3" y="3" width="7.5" height="8.5" rx="1.6" />
    <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.6" />
    <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.6" />
    <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.6" />
  </>,
);

export const ComposeIcon = icon(
  <>
    <path d="M4 20h4.2l9.4-9.4a2.1 2.1 0 0 0-3-3L5.2 17v3Z" />
    <path d="M14.5 5.8 18.2 9.5" />
  </>,
);

export const StudioIcon = icon(
  <>
    <path d="m12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z" />
    <path d="m18 15.5.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1Z" />
  </>,
);

export const CalendarIcon = icon(
  <>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" />
    <path d="M3.5 9.8h17M8.2 3v4M15.8 3v4" />
  </>,
);

export const PostsIcon = icon(
  <>
    <rect x="4" y="3.5" width="16" height="17" rx="2.2" />
    <path d="M8 8.5h8M8 12.5h8M8 16.5h4.5" />
  </>,
);

export const MediaIcon = icon(
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.2" />
    <circle cx="8.8" cy="9.8" r="1.6" />
    <path d="m4.2 17.2 4.6-4.4 3.5 3.3 3.1-2.9 4.4 4" />
  </>,
);

export const CaptionsIcon = icon(
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="2.2" />
    <path d="M7.5 10.5h4M7.5 14h9M14.5 10.5h2" />
  </>,
);

export const ConnectionsIcon = icon(
  <>
    <circle cx="6.5" cy="12" r="2.6" />
    <circle cx="17.5" cy="6.5" r="2.6" />
    <circle cx="17.5" cy="17.5" r="2.6" />
    <path d="m8.9 10.8 6.2-3.1M8.9 13.2l6.2 3.1" />
  </>,
);

export const PeopleIcon = icon(
  <>
    <circle cx="9.2" cy="8.5" r="3.1" />
    <path d="M3.5 19.5c0-3 2.6-5 5.7-5s5.7 2 5.7 5" />
    <path d="M16.2 6.2a3 3 0 0 1 0 5.8M17.5 14.9c1.9.5 3.2 2 3.2 4.1" />
  </>,
);

export const OrganisationIcon = icon(
  <>
    <path d="M4 20.5V6.2L12 3.5v17" />
    <path d="M12 20.5h8V10l-8-2.6" />
    <path d="M7.2 9.5h1.6M7.2 13h1.6M7.2 16.5h1.6M15.2 12.5h1.8M15.2 16.5h1.8" />
  </>,
);

export const ClientsIcon = icon(
  <>
    <rect x="3" y="7.5" width="18" height="13" rx="2.2" />
    <path d="M8.8 7.5V5.8a2 2 0 0 1 2-2h2.4a2 2 0 0 1 2 2v1.7" />
    <path d="M3 12.5h18" />
  </>,
);

export const PricingIcon = icon(
  <>
    <path d="m11.2 3.5-7 7a2 2 0 0 0 0 2.8l6.5 6.5a2 2 0 0 0 2.8 0l7-7V3.5h-9.3Z" />
    <circle cx="16.4" cy="8.1" r="1.3" />
  </>,
);

export const BillingIcon = icon(
  <>
    <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
    <path d="M3 10h18M6.8 14.5h3.4" />
  </>,
);

export const ProfileIcon = icon(
  <>
    <circle cx="12" cy="8.5" r="3.4" />
    <path d="M4.8 20.2c0-3.5 3.2-5.8 7.2-5.8s7.2 2.3 7.2 5.8" />
  </>,
);

export const SupportIcon = icon(
  <>
    <circle cx="12" cy="12" r="8.7" />
    <path d="M9.6 9.6a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.8-.9 1.4v.4" />
    <path d="M12 17.1h.01" />
  </>,
);

/** The sidebar toggle. One glyph — the sidebar's state is announced, not drawn. */
export const MenuIcon = icon(<path d="M4 7h16M4 12h16M4 17h16" />);

/** Put this panel away. Points the way it moves, which a hamburger cannot. */
export const CollapseIcon = icon(<path d="m13.5 6-6 6 6 6M19 6l-6 6 6 6" />);

export const SearchIcon = icon(
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </>,
);

export const PlusIcon = icon(<path d="M12 5.5v13M5.5 12h13" />);

export const StarIcon = icon(
  <path d="m12 3.8 2.5 5.1 5.6.8-4 3.9 1 5.6-5.1-2.7-5 2.7 1-5.6-4.1-3.9 5.6-.8L12 3.8Z" />,
);
